import { useState, useEffect, type Dispatch, type SetStateAction } from 'react';

import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import { token } from '@atlaskit/tokens';

import { useAccountCreationContext } from '@/components/signup/account-creation/hooks/account-creation-context';
import { emailIsValid } from '@/components/signup/utils/email-is-valid';
import { fontSize } from '@/components/signup/utils/font-size';
import { wacToken } from '@/libraries/design/foundations';

import { InlineInputV2 } from './inline-input-v2';
import { InlineSeparator } from './inline-separator';
import { InlineSubmit } from './inline-submit';
import { SocialButton } from './social-button';

/** Atlaskit `media.above.md` — horizontal desktop layout at 1024px+ */
const DESKTOP_MQ = '(min-width: 64rem)';

type FormFieldsProps = {
	email: string;
	setEmail: Dispatch<SetStateAction<string>>;
	isValid: boolean | undefined;
	errorMessageText: string;
};

/**
 * Homepage Simplified form V2 (TWCG-2414).
 * Dual trees (no layout media queries) so Compiled can't half-apply desktop styles:
 * - stacked (&lt; md / tablet+mobile): email → Sign up → or → Google
 * - desktop (md+): grey outer pill, white nested field + or + Google
 * @see https://www.figma.com/design/KhAdeSJF2y30N2lm8qmRmi/Homepage-JIra-Signup?node-id=105-3759
 */
export const InlineInputSubmitSocialV2 = () => {
	const intl = useIntl();
	const { isReadyForInvalid, setIsEmailValid, typeAheadEmail } = useAccountCreationContext();
	const [email, setEmail] = useState(typeAheadEmail);
	const [isValid, setValid] = useState<boolean>();
	const [isDesktop, setIsDesktop] = useState(() =>
		typeof window !== 'undefined' ? window.matchMedia(DESKTOP_MQ).matches : false,
	);

	const errorMessageText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.invalidEmail',
		defaultMessage: 'Please enter a valid email address.',
	});

	const labelText = intl.formatMessage({
		id: 'BxpUiSignup.SignupForm.enterWorkEmail',
		defaultMessage: 'Enter work email',
	});

	useEffect(() => {
		const mediaQuery = window.matchMedia(DESKTOP_MQ);
		const onChange = () => setIsDesktop(mediaQuery.matches);
		onChange();
		mediaQuery.addEventListener('change', onChange);
		return () => mediaQuery.removeEventListener('change', onChange);
	}, []);

	useEffect(() => {
		if (!email.trim()) {
			setValid(undefined);
			setIsEmailValid(false);
			return;
		}
		const valid = emailIsValid(email);
		if (!valid && !isReadyForInvalid) {
			setValid(undefined);
			return;
		}
		setValid(valid);
		setIsEmailValid(valid);
	}, [email, setIsEmailValid, isReadyForInvalid]);

	const fields: FormFieldsProps = {
		email,
		setEmail,
		isValid,
		errorMessageText,
	};

	return (
		<div css={containerCss} data-testid="inline-input-submit-social-v2">
			<label css={srOnlyCss} htmlFor="inline_email_embedded_signup_input">
				{labelText}
			</label>
			{isDesktop ? (
				<>
					<DesktopForm {...fields} />
					{/* Desktop: below the outer pill (field + or + Google stay one row) */}
					{isValid === false ? (
						<div css={errorCss} role="alert">
							{errorMessageText}
						</div>
					) : null}
				</>
			) : (
				<StackedForm {...fields} />
			)}
		</div>
	);
};

const EmailField = ({ email, setEmail, isValid, errorMessageText }: FormFieldsProps) => (
	<InlineInputV2
		email={email}
		setEmail={setEmail}
		isValid={isValid}
		errorMessageText={errorMessageText}
	/>
);

const DesktopForm = (props: FormFieldsProps) => (
	<div css={desktopLayoutCss}>
		<div
			css={[
				desktopFieldCss,
				props.isValid === undefined && desktopFieldFocusCss,
				props.isValid === false && desktopFieldInvalidCss,
				props.isValid === true && desktopFieldValidCss,
			]}
		>
			<div css={[fieldInputChromeCss, desktopInputCss]}>
				<EmailField {...props} />
			</div>
			<div css={desktopSubmitCss}>
				<InlineSubmit />
			</div>
		</div>
		<div css={[separatorCss, desktopSeparatorCss]}>
			<InlineSeparator />
		</div>
		<div css={[socialButtonCss, desktopSocialCss]}>
			<SocialButton />
		</div>
	</div>
);

const StackedForm = (props: FormFieldsProps) => (
	<div css={stackedLayoutCss}>
		{/* Field + error grouped so alert sits under the input, not under Google */}
		<div css={stackedFieldGroupCss}>
			<div
				css={[
					stackedFieldCss,
					props.isValid === undefined && stackedFieldFocusCss,
					props.isValid === false && stackedFieldInvalidCss,
					props.isValid === true && stackedFieldValidCss,
				]}
			>
				<div css={[fieldInputChromeCss, stackedInputCss]}>
					<EmailField {...props} />
				</div>
			</div>
			{props.isValid === false ? (
				<div css={stackedErrorCss} role="alert">
					{props.errorMessageText}
				</div>
			) : null}
		</div>
		<div css={stackedSubmitCss}>
			<InlineSubmit />
		</div>
		<div css={[separatorCss, stackedSeparatorCss]}>
			<InlineSeparator />
		</div>
		<div css={[socialButtonCss, stackedSocialCss]}>
			<SocialButton />
		</div>
	</div>
);

const containerCss = css({
	width: '100%',
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
});

const srOnlyCss = css({
	border: 0,
	clip: 'rect(0 0 0 0)',
	height: '1px',
	margin: '-1px',
	overflow: 'hidden',
	padding: 0,
	position: 'absolute',
	width: '1px',
	whiteSpace: 'nowrap',
});

/** Desktop (Figma): outer pill min 680 / max 905 / height 65 */
const desktopLayoutCss = css({
	// Avoid parent-column stretch; slack from min 680 stays trailing, not in email
	alignSelf: 'flex-start',
	width: 'max(680px, max-content)',
	maxWidth: '905px',
	height: '65px',
	display: 'flex',
	alignItems: 'center',
	gap: '10px',
	backgroundColor: wacToken('color.neutral.light200'),
	borderRadius: 100,
	boxSizing: 'border-box',
	padding: token('space.100'),
});

/** Tablet/mobile: stacked — Figma min 300 / max 490 */
const stackedLayoutCss = css({
	width: '100%',
	minWidth: '300px',
	maxWidth: '490px',
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.100'),
});

const stackedFieldGroupCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	gap: token('space.050'),
	width: '100%',
});

const desktopFieldCss = css({
	display: 'flex',
	// Figma Form Field min 485 — hug content so longer CTAs grow outward
	flex: '0 0 auto',
	alignItems: 'center',
	gap: token('space.100'),
	width: 'max-content',
	minWidth: '485px',
	height: '49px',
	backgroundColor: wacToken('color.neutral.light0'),
	// Stable 2px border-box; light200 matches outer pill (no white halo);
	// inset 1px Neutral400 is the default visible edge
	border: `2px solid ${wacToken('color.neutral.light200')}`,
	boxShadow: `inset 0 0 0 1px ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	boxSizing: 'border-box',
	paddingBlock: '4px',
	paddingInlineEnd: '2px',
	paddingInlineStart: token('space.200'),
});

const stackedFieldCss = css({
	display: 'flex',
	flexDirection: 'column',
	alignItems: 'stretch',
	width: '100%',
});

/** Figma Active/focus / Error / Success: solid 2px (no inset); content box stays fixed */
const desktopFieldFocusCss = css({
	// Sign up lives inside this pill — don't ring the field when the button is focused
	// (Button already has its own focus-visible outline).
	['&:has(input:focus)']: {
		borderColor: token('color.border.focused'),
		boxShadow: 'none',
	},
});

const stackedFieldFocusCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type:focus-within']: {
		borderColor: token('color.border.focused'),
		boxShadow: 'none',
	},
});

const desktopFieldInvalidCss = css({
	borderColor: token('color.border.danger'),
	boxShadow: 'none',
});

const stackedFieldInvalidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: token('color.border.danger'),
		boxShadow: 'none',
	},
});

const desktopFieldValidCss = css({
	borderColor: token('color.border.success'),
	boxShadow: 'none',
});

const stackedFieldValidCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:first-of-type']: {
		borderColor: token('color.border.success'),
		boxShadow: 'none',
	},
});
/** Shared input chrome (desktop + stacked) */
const fieldInputChromeCss = css({
	boxSizing: 'border-box',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div']: {
		backgroundColor: 'transparent !important',
		border: 'none !important',
		borderRadius: '0 !important',
		boxShadow: 'none !important',
		height: '100% !important',
		width: '100%',
	},
	// Kill Atlaskit TextField focus inset — outer pill owns the ring
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div:focus-within, & > div:focus, & div[data-ds--text-field--container]']: {
		backgroundColor: 'transparent !important',
		border: 'none !important',
		boxShadow: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		fontSize: '16px !important',
		fontWeight: 400,
		letterSpacing: '-0.16px',
		paddingInlineStart: '0 !important',
		paddingInlineEnd: '0 !important',
		boxShadow: 'none !important',
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input:focus, input:focus-visible']: {
		boxShadow: 'none !important',
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input::placeholder']: {
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
	},
});

const desktopInputCss = css({
	// Prefer ~340 so Form Field min 485 is filled with English Sign up (little/no
	// slack for longer CTAs to reclaim). Grow to keep button flush; never shrink
	// below basis — field/outer grow instead. Figma floor remains minWidth 305.
	flex: '1 0 340px',
	height: '100%',
	minWidth: '305px',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['& > div']: {
		minWidth: 0,
		maxWidth: '100%',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['input']: {
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
});

const stackedInputCss = css({
	width: '100%',
	height: '40px',
	backgroundColor: wacToken('color.neutral.light0'),
	// Stable 2px border-box; 1px Neutral400 edge via inset (page bg is white)
	border: `2px solid ${wacToken('color.neutral.light0')}`,
	boxShadow: `inset 0 0 0 1px ${wacToken('color.neutral.light400')}`,
	borderRadius: 50,
	boxSizing: 'border-box',
	paddingInlineStart: token('space.200'),
	paddingInlineEnd: token('space.200'),
});

/** Fit shared InlineSubmit into the 49px field; clear its -1px margins */
const submitFocusVisibleCss = {
	outline: `2px solid ${token('color.border.focused')} !important`,
	outlineOffset: '2px !important',
	boxShadow: `0 0 0 2px ${wacToken('color.neutral.light0')} !important`,
} as const;

const desktopSubmitCss = css({
	flexShrink: 0,
	display: 'flex',
	alignItems: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
		margin: '0 !important',
		maxWidth: 'none !important',
		minWidth: 'auto !important',
		width: 'auto !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus-visible']: submitFocusVisibleCss,
});

const stackedSubmitCss = css({
	width: '100%',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		margin: '0 !important',
		maxWidth: '100% !important',
		width: '100% !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus-visible']: submitFocusVisibleCss,
});

/** Shared "or" separator text */
const separatorCss = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['span']: {
		fontSize: '16px',
		letterSpacing: '-0.16px',
		lineHeight: 1.56,
	},
});

const desktopSeparatorCss = css({
	// Hug translated "or" (e.g. または); layout gap keeps 10px on either side
	flexShrink: 0,
	whiteSpace: 'nowrap',
});

const stackedSeparatorCss = css({
	height: '24px',
});

/** Shared Google button styles */
const socialButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		border: `1px solid ${wacToken('color.neutral.light400')} !important`,
		borderRadius: '40px !important',
		color: `${wacToken('color.primary.black')} !important`,
		fontSize: '16px !important',
		fontWeight: '400 !important',
		gap: `${token('space.100')} !important`,
		letterSpacing: '-0.16px',
		margin: '0 !important',
		maxWidth: 'none !important',
		paddingBlock: '11px !important',
		paddingInline: '24px !important',
		width: 'auto !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:hover']: {
		backgroundColor: `${wacToken('color.neutral.light200')} !important`,
	},
	// Figma Accessibility focus: solid 2px color/focus
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button:focus, button:focus-visible']: {
		backgroundColor: `${wacToken('color.neutral.light0')} !important`,
		borderColor: `${token('color.border.focused')} !important`,
		boxShadow: `inset 0 0 0 1px ${token('color.border.focused')} !important`,
		outline: 'none !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['svg']: {
		height: '20px',
		minHeight: '20px',
		minWidth: '20px',
		width: '20px',
	},
});

const desktopSocialCss = css({
	width: 'auto',
	alignSelf: 'stretch',
	flexShrink: 0,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '100% !important',
		minHeight: '49px !important',
	},
});

const stackedSocialCss = css({
	width: '100%',
	display: 'flex',
	justifyContent: 'center',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['button']: {
		height: '41px !important',
		minHeight: '41px !important',
	},
});

const errorTextCss = {
	color: token('color.text.danger'),
	fontSize: fontSize.xs,
	lineHeight: 1.5,
} as const;

const errorCss = css({
	...errorTextCss,
	marginInlineStart: '24px',
	marginInlineEnd: '24px',
});

/** Stacked: under the email field, indented past the envelope icon */
const stackedErrorCss = css({
	...errorTextCss,
	marginInlineStart: token('space.200'),
});
